import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2, Mail, Phone, MapPin } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactForm() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Bitte gib deinen Namen an.";
    if (!form.email.trim()) e.email = "Bitte gib deine E-Mail an.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = "Bitte gib eine gültige E-Mail an.";
    if (!form.message.trim() || form.message.trim().length < 10)
      e.message = "Bitte schreib uns kurz (min. 10 Zeichen), worum es geht.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onChange = (k) => (ev) => {
    setForm((f) => ({ ...f, [k]: ev.target.value }));
    if (errors[k]) setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      setSuccess(true);
      setForm({ name: "", email: "", message: "" });
      toast.success("Vielen Dank! Deine Anfrage ist eingegangen.", {
        description: "Alex meldet sich in der Regel innerhalb von 48 Stunden.",
      });
    } catch (err) {
      toast.error("Ups, das hat nicht geklappt.", {
        description:
          err?.response?.data?.detail ||
          "Bitte versuche es gleich nochmal oder schreib direkt eine E-Mail.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact-form-section"
      data-testid="contact-form-section"
      className="bg-[#F7F9FB] text-[#1F2937] py-24 lg:py-32"
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Left intro */}
          <div className="lg:col-span-5">
            <p className="text-xs sm:text-sm font-semibold tracking-[0.18em] uppercase text-[#2E89C4] mb-5">
              Kontakt
            </p>
            <h2
              data-testid="contact-title"
              className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.1]"
            >
              Schreib mir.{" "}
              <span className="text-[#6FB04F]">Ich antworte persönlich.</span>
            </h2>
            <p className="mt-6 text-[#1F2937]/70 leading-relaxed">
              Erzähl mir kurz, wo du gerade stehst – und was du dir wünschst.
              Wir finden gemeinsam heraus, ob und wie ich dir helfen kann.
            </p>

            <ul className="mt-10 space-y-4">
              <li className="flex items-center gap-4">
                <span className="h-10 w-10 rounded-full bg-[#2E89C4]/10 flex items-center justify-center">
                  <Mail size={16} className="text-[#2E89C4]" />
                </span>
                <span className="text-sm">info@alex-buchner-health.de</span>
              </li>
              <li className="flex items-center gap-4">
                <span className="h-10 w-10 rounded-full bg-[#2E89C4]/10 flex items-center justify-center">
                  <Phone size={16} className="text-[#2E89C4]" />
                </span>
                <span className="text-sm">+49 (0) 175 1515 725</span>
              </li>
              <li className="flex items-center gap-4">
                <span className="h-10 w-10 rounded-full bg-[#2E89C4]/10 flex items-center justify-center">
                  <MapPin size={16} className="text-[#2E89C4]" />
                </span>
                <span className="text-sm">Chiemgau · Vor Ort & online</span>
              </li>
            </ul>
          </div>

          {/* Right form */}
          <div className="lg:col-span-7">
            <form
              data-testid="contact-form"
              onSubmit={onSubmit}
              className="rounded-3xl border border-[#1F2937]/10 bg-white/70 backdrop-blur-sm p-7 lg:p-10 shadow-[0_30px_80px_-40px_rgba(43,43,43,0.35)]"
              noValidate
            >
              {success ? (
                <div
                  data-testid="contact-success"
                  className="text-center py-10"
                >
                  <div className="mx-auto h-14 w-14 rounded-full bg-[#6FB04F]/15 flex items-center justify-center mb-5">
                    <Send size={22} className="text-[#6FB04F]" />
                  </div>
                  <h3 className="font-display text-2xl font-bold mb-3">
                    Danke! Deine Anfrage ist eingegangen.
                  </h3>
                  <p className="text-[#1F2937]/70 max-w-md mx-auto">
                    Alex meldet sich in der Regel innerhalb von 48 Stunden per
                    E-Mail bei dir.
                  </p>
                  <button
                    type="button"
                    data-testid="contact-reset-button"
                    onClick={() => setSuccess(false)}
                    className="mt-7 text-sm font-semibold text-[#2E89C4] hover:underline"
                  >
                    Weitere Nachricht senden
                  </button>
                </div>
              ) : (
                <>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-semibold tracking-wide uppercase text-[#1F2937]/60 mb-2">
                        Name
                      </label>
                      <input
                        type="text"
                        data-testid="contact-input-name"
                        value={form.name}
                        onChange={onChange("name")}
                        placeholder="Dein Name"
                        className={`w-full bg-[#F7F9FB] border ${
                          errors.name
                            ? "border-red-500"
                            : "border-[#1F2937]/15"
                        } rounded-xl px-4 py-3.5 text-[15px] focus:outline-none focus:border-[#2E89C4] focus:ring-2 focus:ring-[#2E89C4]/20 transition`}
                      />
                      {errors.name && (
                        <p
                          data-testid="contact-error-name"
                          className="mt-1.5 text-xs text-red-600"
                        >
                          {errors.name}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs font-semibold tracking-wide uppercase text-[#1F2937]/60 mb-2">
                        E-Mail
                      </label>
                      <input
                        type="email"
                        data-testid="contact-input-email"
                        value={form.email}
                        onChange={onChange("email")}
                        placeholder="du@beispiel.de"
                        className={`w-full bg-[#F7F9FB] border ${
                          errors.email
                            ? "border-red-500"
                            : "border-[#1F2937]/15"
                        } rounded-xl px-4 py-3.5 text-[15px] focus:outline-none focus:border-[#2E89C4] focus:ring-2 focus:ring-[#2E89C4]/20 transition`}
                      />
                      {errors.email && (
                        <p
                          data-testid="contact-error-email"
                          className="mt-1.5 text-xs text-red-600"
                        >
                          {errors.email}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="mt-5">
                    <label className="block text-xs font-semibold tracking-wide uppercase text-[#1F2937]/60 mb-2">
                      Nachricht
                    </label>
                    <textarea
                      data-testid="contact-input-message"
                      value={form.message}
                      onChange={onChange("message")}
                      placeholder="Wo stehst du gerade? Was wünschst du dir?"
                      rows={6}
                      className={`w-full bg-[#F7F9FB] border ${
                        errors.message
                          ? "border-red-500"
                          : "border-[#1F2937]/15"
                      } rounded-xl px-4 py-3.5 text-[15px] focus:outline-none focus:border-[#2E89C4] focus:ring-2 focus:ring-[#2E89C4]/20 transition resize-none`}
                    />
                    {errors.message && (
                      <p
                        data-testid="contact-error-message"
                        className="mt-1.5 text-xs text-red-600"
                      >
                        {errors.message}
                      </p>
                    )}
                  </div>

                  <div className="mt-7 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
                    <p className="text-xs text-[#1F2937]/55 max-w-sm">
                      Deine Daten werden ausschließlich für die Kontaktaufnahme
                      verwendet.
                    </p>
                    <button
                      type="submit"
                      data-testid="contact-submit-button"
                      disabled={loading}
                      className="btn-primary inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full text-sm font-semibold disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <>
                          <Loader2 size={16} className="animate-spin" />
                          Wird gesendet…
                        </>
                      ) : (
                        <>
                          Nachricht senden
                          <Send size={15} />
                        </>
                      )}
                    </button>
                  </div>
                </>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
